import type { ComputedRef, Ref } from 'vue'
import type { ID, Maybe } from '@/types'
import type {
  CurtainLoginState,
  CustomerAccount,
  ManagerAccount,
  ToastState,
} from '@/types/ui'
import { isNumber } from '@/types/utilities'

import { ref, unref, watch } from 'vue'
import { syncRef } from '@vueuse/core'
import { useRouter } from 'vue-router'

import { defineStore, storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useCustomerAuthStore } from '@/stores/customer/auth'
import { useCartStore } from '@/stores/customer/cart'

import { clone } from '@/helpers'

import { CURTAIN_LOGIN_DEFAULT_STATE, DEFAULT_TOAST_STATE } from '@/constants'
import { goal }                                             from "@/plugins/metrika";

const MANAGER_STORE_KEY = 'seen-manager'
const CUSTOMER_STORE_KEY = 'seen-customer'

export const useUiStore = defineStore('ui', () => {
  const router = useRouter()
  /**
   * Manager auth
   * */
  const authStore = useAuthStore()
  const { authToken, currentUser } = storeToRefs(authStore)
  /**
   * Show (manager)
   * */
  const showHiddenBlocks = ref(true)
  const showHiddenPosts = ref(true)
  const showHiddenCatalogs = ref(true)
  const showHiddenBrands = ref(true)
  const showHiddenPresets = ref(true)
  const showOnlySingleProducts = ref(false)
  const showProductColors = ref(true)
  const managerTimestamp = ref<Maybe<number>>(null)
  /**
   * Customer auth
   * */
  const customerAuthStore = useCustomerAuthStore()
  const { authToken: customerAuthToken, currentUser: currentCustomer } =
    storeToRefs(customerAuthStore)
  /**
   * Captcha
   * */
  const captchaOptions = ref<{
    location: Maybe<'login' | 'oneClick'>
    fn: Maybe<(token: string) => void>
  }>({ location: null, fn: null })
  /**
   * Curtain Login (customer)
   * */
  const loginVisibility = ref(false)
  const curtainState = ref<CurtainLoginState>(
    clone(CURTAIN_LOGIN_DEFAULT_STATE)
  )

  function showCurtainLogin(checkout?: boolean) {
    if (checkout) {
      curtainState.value.checkout = true
      curtainState.value.tab = 'beforeCheckout'
    }

    loginVisibility.value = true
  }

  function resetCurtainLogin() {
    curtainState.value = clone(CURTAIN_LOGIN_DEFAULT_STATE)
  }
  ////
  const searchVisibility = ref(false)
  /**
   * Popup-banner
   * */
  const popupBannerVisibility = ref(false)
  const popupBannerVisibility2 = ref(false)
  const lastPopupTimestamp = ref<CustomerAccount['lastPopupTimestamp']>(null)
  const subscribed = ref<CustomerAccount['subscribed']>(false)

  function saveToSessionStorage(key: string, value: any) {
    sessionStorage.setItem(
      `${CUSTOMER_STORE_KEY}-${key}`,
      JSON.stringify(value)
    )
  }
  function getFromSessionStorage<T = any>(key: string) {
    return JSON.parse(
      sessionStorage.getItem(`${CUSTOMER_STORE_KEY}-${key}`) ?? 'null'
    ) as Maybe<T>
  }
  /**
   * Cookie toast
   * */
  const cookieAccepted = ref<CustomerAccount['cookieAccepted']>(false)

  const cookieToastState = ref<Partial<ToastState>>({
    visibility: false,
  })

  function toggleCookieToast() {
    cookieToastState.value.visibility = !cookieToastState.value.visibility
  }
  /**
   * Cart
   * */
  const cartVisibility = ref(false)

  const cartStore = useCartStore()
  const { cartItems, coupon } = storeToRefs(cartStore)
  /**
   * Product favorites
   * */
  const favorites = ref<CustomerAccount['favorites']>([])
  const customerTimestamp = ref<Maybe<number>>(null)

  function switchFavorite(id?: ID) {
    if (!Number.isInteger(id)) return

    isNumber(id)

    const index = getFavoriteIndex(id)

    if (index !== -1) favorites.value.splice(index, 1)
    else {
      favorites.value.push(id)
      goal('favorites_add')
    }
  }

  function getFavoriteIndex(id: ID) {
    return favorites.value.findIndex((favoriteId) => favoriteId === id)
  }
  /**
   * Init ui storage logic
   * */
  getFromLocalStorage()
  /**
   * Manager account
   * */
  watch(
    (): Omit<ManagerAccount, 'timestamp'> => ({
      showHiddenBlocks: showHiddenBlocks.value,
      showHiddenPosts: showHiddenPosts.value,
      showHiddenCatalogs: showHiddenCatalogs.value,
      showHiddenBrands: showHiddenBrands.value,
      showHiddenPresets: showHiddenPresets.value,
      showOnlySingleProducts: showOnlySingleProducts.value,
      showProductColors: showProductColors.value,

      authToken: authToken.value,
      currentUser: currentUser.value,
    }),
    () => saveToLocalStorage('manager')
  )
  /**
   * Customer account
   * */
  watch(
    () => [
      favorites.value.length,
      cartItems.value,
      coupon.value,
      customerAuthToken.value,
      currentCustomer.value,
      lastPopupTimestamp.value,
      subscribed.value,
      cookieAccepted.value,
    ],
    () => saveToLocalStorage('customer'),
    { deep: true }
  )

  function saveToLocalStorage(type: 'manager' | 'customer') {
    const managerAccount: ManagerAccount = {
      showHiddenBlocks: unref(showHiddenBlocks),
      showHiddenPosts: unref(showHiddenPosts),
      showHiddenCatalogs: unref(showHiddenCatalogs),
      showHiddenBrands: unref(showHiddenBrands),
      showHiddenPresets: unref(showHiddenPresets),
      showOnlySingleProducts: unref(showOnlySingleProducts),
      showProductColors: unref(showProductColors),

      authToken: unref(authToken),
      currentUser: unref(currentUser),

      timestamp: new Date().getTime(),
    }
    const customerAccount: CustomerAccount = {
      cartItems: unref(cartItems),
      coupon: unref(coupon),
      favorites: unref(favorites),

      authToken: unref(customerAuthToken),
      currentUser: unref(currentCustomer),

      lastPopupTimestamp: unref(lastPopupTimestamp),
      subscribed: unref(subscribed),
      cookieAccepted: unref(cookieAccepted),

      timestamp: new Date().getTime(),
    }

    const key = type === 'manager' ? MANAGER_STORE_KEY : CUSTOMER_STORE_KEY
    const data = JSON.stringify(
      type === 'manager' ? managerAccount : customerAccount
    )

    localStorage.setItem(key, data)
  }

  function getFromLocalStorage() {
    const managerAccount: ManagerAccount = JSON.parse(
      localStorage.getItem(MANAGER_STORE_KEY) ?? '{}'
    )
    const customerAccount: CustomerAccount = JSON.parse(
      localStorage.getItem(CUSTOMER_STORE_KEY) ?? '{}'
    )
    /**
     * Manager
     * */
    showHiddenBlocks.value = managerAccount.showHiddenBlocks ?? true
    showHiddenPosts.value = managerAccount.showHiddenPosts ?? true
    showHiddenCatalogs.value = managerAccount.showHiddenCatalogs ?? true
    showHiddenBrands.value = managerAccount.showHiddenCatalogs ?? true
    showHiddenPresets.value = managerAccount.showHiddenPresets ?? true
    showOnlySingleProducts.value =
      managerAccount.showOnlySingleProducts ?? false
    showProductColors.value = managerAccount.showProductColors ?? true

    authToken.value = managerAccount.authToken ?? null
    currentUser.value = managerAccount.currentUser ?? null

    managerTimestamp.value = managerAccount.timestamp ?? null
    /**
     * Customer
     * */
    cartItems.value = customerAccount.cartItems ?? []
    coupon.value = customerAccount.coupon ?? null
    favorites.value = customerAccount.favorites ?? []

    customerAuthToken.value = customerAccount.authToken ?? null
    currentCustomer.value = customerAccount.currentUser ?? null

    lastPopupTimestamp.value = customerAccount.lastPopupTimestamp ?? null
    subscribed.value = customerAccount.subscribed ?? false
    cookieAccepted.value = customerAccount.cookieAccepted ?? false

    customerTimestamp.value = customerAccount.timestamp ?? null
  }
  /**
   * Hide scroll
   * */
  const overflowHidden = ref(false)

  watch(() => overflowHidden.value, switchOverflowHidden)

  function switchOverflowHidden() {
    const body = document.querySelector('body')
    if (!body) return

    if (overflowHidden.value && !body.classList.contains('overflow-hidden')) {
      body.classList.add('overflow-hidden')
    } else if (
      !overflowHidden.value &&
      body.classList.contains('overflow-hidden')
    ) {
      const timeoutId = setTimeout(() => {
        body.classList.remove('overflow-hidden')

        clearTimeout(timeoutId)
      }, 200)
    }
  }
  /**
   * Toast
   * */
  const toastState = ref<ToastState>(clone(DEFAULT_TOAST_STATE))

  function showToast(options: Partial<ToastState>) {
    toastState.value = { ...toastState.value, ...options, visibility: true }
  }
  function resetToast() {
    toastState.value = clone(DEFAULT_TOAST_STATE)
  }

  watch(
    () => toastState.value.visibility,
    (newVal) => {
      if (!newVal) return

      const timeoutId = setTimeout(() => {
        toastState.value.visibility = false

        const innerTimeoutId = setTimeout(() => {
          resetToast()

          clearTimeout(innerTimeoutId)
          clearTimeout(timeoutId)
        }, 300)
      }, toastState.value.timeout)
    }
  )
  /**
   * Ticker 1 & 2
   * */
  const ticker1Visibility = ref(false)
  const ticker2Visibility = ref(false)
  const ticker2VisibilityByPage = ref(false)

  function setTickerVisibility(
    isVisible: ComputedRef<boolean> | Ref<boolean>,
    number: 1 | 2 | 3 = 1
  ) {
    const arr = [
      null,
      ticker1Visibility,
      ticker2Visibility,
      ticker2VisibilityByPage,
    ]

    syncRef(isVisible, arr[number] as Ref<boolean>, {
      direction: 'ltr',
    })
  }
  /**
   * Manager Blog ProductList
   * */
  const modalProductListVisibility = ref(false)
  const sidebarProductListVisibility = ref(false)
  /**
   * Manager Block
   * */
  const modalCatalogVisibility = ref(false)

  return {
    router,
    /**
     * Manager
     * */
    showHiddenBlocks,
    showHiddenPosts,
    showHiddenCatalogs,
    showHiddenBrands,
    showHiddenPresets,
    showOnlySingleProducts,
    showProductColors,
    managerTimestamp,
    /**
     * Customer
     * */
    captchaOptions,

    loginVisibility,
    curtainState,
    showCurtainLogin,
    resetCurtainLogin,

    searchVisibility,
    popupBannerVisibility,
    popupBannerVisibility2,
    lastPopupTimestamp,
    subscribed,

    cookieAccepted,
    cookieToastState,
    toggleCookieToast,

    saveToSessionStorage,
    getFromSessionStorage,

    cartVisibility,
    favorites,
    customerTimestamp,

    getFavoriteIndex,
    switchFavorite,

    saveToLocalStorage,
    getFromLocalStorage,

    overflowHidden,

    toastState,
    showToast,
    resetToast,

    ticker1Visibility,
    ticker2Visibility,
    ticker2VisibilityByPage,
    setTickerVisibility,

    modalProductListVisibility,
    sidebarProductListVisibility,
    modalCatalogVisibility,
  }
})
